import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import logger from '../lib/logger';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SESSION_TOKEN_KEY = 'seertech_session_token';
const IMPERSONATION_KEY = 'seertech_impersonation';

// In-memory auth token store
let _inMemoryToken = null;

// On startup, restore token from sessionStorage (survives refresh within same tab)
try {
  const stored = sessionStorage.getItem(SESSION_TOKEN_KEY);
  if (stored) _inMemoryToken = stored;
} catch { /* private browsing or disabled */ }

// Configure axios defaults
axios.defaults.withCredentials = true;

// ── Custom-domain same-origin enforcement (Feb 2026) ────────────────
// When the app is served from a user-mapped custom domain (e.g.
// https://blueprint.seerios.ai) but the build-time REACT_APP_BACKEND_URL
// still points at the Emergent host (https://*.emergent.host), every
// API call becomes cross-origin with credentials. The Emergent ingress
// proxy adds `Access-Control-Allow-Origin: *` on top of our middleware's
// headers, which browsers reject for credentialed requests — causing
// Google OAuth + every other authed call to fail silently on the custom
// domain.
//
// Fix: rewrite the API host to match the page's host so every call is
// genuinely same-origin (Emergent's domain mapping already routes
// `/api/*` on the custom domain to the same backend). Cross-origin
// CORS never gets evaluated → bug is bypassed cleanly.
axios.interceptors.request.use((config) => {
  try {
    if (typeof window === 'undefined') return config;
    const pageHost = window.location.host;
    let target = null;
    if (config.url && /^https?:\/\//i.test(config.url)) {
      target = new URL(config.url);
    } else if (config.baseURL && /^https?:\/\//i.test(config.baseURL)) {
      target = new URL(config.url || '', config.baseURL);
    }
    if (target && target.host !== pageHost) {
      target.protocol = window.location.protocol;
      target.host = pageHost;
      config.url = target.toString();
      config.baseURL = undefined;
    }
  } catch { /* leave URL untouched */ }
  return config;
});

// v392 — silent session renewal: when an access token expires (401) on a
// non-auth request, refresh once via the 30-day refresh cookie and retry.
let _refreshing = null;
axios.interceptors.response.use(
  (res) => res,
  async (error) => {
    const cfg = error?.config;
    const status = error?.response?.status;
    const url = cfg?.url || '';
    if (status !== 401 || !cfg || cfg._retried || /\/api\/auth\//.test(url) || _inMemoryToken) {
      return Promise.reject(error);
    }
    cfg._retried = true;
    try {
      _refreshing = _refreshing || axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true, _retried: true })
        .finally(() => { _refreshing = null; });
      await _refreshing;
      return axios(cfg);
    } catch {
      return Promise.reject(error);
    }
  }
);

// Axios interceptor: attach Bearer token as fallback when cookies aren't sent
axios.interceptors.request.use((config) => {
  if (_inMemoryToken && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${_inMemoryToken}`;
  }
  return config;
});

function _persistToken(token) {
  _inMemoryToken = token;
  try { sessionStorage.setItem(SESSION_TOKEN_KEY, token); } catch { /* ignore */ }
}

function _clearToken() {
  _inMemoryToken = null;
  try { sessionStorage.removeItem(SESSION_TOKEN_KEY); } catch { /* ignore */ }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated, object = authenticated
  const [loading, setLoading] = useState(true);
  const [impersonation, setImpersonation] = useState(() => {
    try {
      const stored = sessionStorage.getItem(IMPERSONATION_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  });

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=') || window.location.search?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    // Retry transient failures (backend busy with a heavy parse, network blips):
    // only a real 401/403 means the session is invalid and should be cleared.
    let triedRefresh = false;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const response = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true, timeout: 15000 });
        setUser(response.data);
        // If we got a session_token back, persist it
        if (response.data?.session_token) {
          _persistToken(response.data.session_token);
        }
        // Lazy-load display-name overrides so group labels render correctly
        // everywhere after login. Non-blocking: failure is silently ignored.
        try {
          const { setGroupLabelOverrides } = await import('../lib/groupLabels');
          const res = await axios.get(`${API_URL}/api/settings/display-names`, { withCredentials: true });
          setGroupLabelOverrides(res.data?.overrides || {});
        } catch { /* best-effort */ }
        setLoading(false);
        return;
      } catch (error) {
        const status = error?.response?.status;
        // v392 — expired access token but a live 30-day refresh cookie: renew silently and retry once.
        if (status === 401 && !_inMemoryToken && !triedRefresh) {
          triedRefresh = true;
          try {
            await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true, _retried: true });
            attempt -= 1;
            continue;
          } catch { /* fall through to sign-out */ }
        }
        if (status === 401 || status === 403) {
          _clearToken();
          setUser(false);
          setLoading(false);
          return;
        }
        // timeout / 5xx / network error — keep the session token and retry
        if (attempt < 2) {
          await new Promise(r => setTimeout(r, 2500 * (attempt + 1)));
        }
      }
    }
    // Still unreachable: show login without destroying the stored session,
    // so a later refresh recovers it once the server settles.
    setUser(false);
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    _clearToken();
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password }, { withCredentials: true });
    setUser(response.data);
    return response.data;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processGoogleSession = async (sessionId) => {
    const response = await axios.post(`${API_URL}/api/auth/google/session`, { session_id: sessionId }, { withCredentials: true });
    // Persist session token (cookie is primary, sessionStorage + in-memory as backup)
    if (response.data.session_token) {
      _persistToken(response.data.session_token);
    }
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      logger.error('Logout error:', error);
    }
    sessionStorage.removeItem(IMPERSONATION_KEY);
    _clearToken();
    setImpersonation(null);
    setUser(false);
  };

  const impersonateUser = async (targetEmail) => {
    const response = await axios.post(`${API_URL}/api/admin/impersonate`, { email: targetEmail }, { withCredentials: true });
    const data = response.data;
    // Store original admin info for restoration
    const adminInfo = { id: user.id, email: user.email, name: user.name, role: user.role, picture: user.picture, auth_type: user.auth_type };
    const impersonationData = { admin: adminInfo, target: { email: data.email, name: data.name, role: data.role, org_id: data.org_id, organization_name: data.organization_name }, audit_id: data.audit_id };
    sessionStorage.setItem(IMPERSONATION_KEY, JSON.stringify(impersonationData));
    setImpersonation(impersonationData);
    setUser(data);
    return data;
  };

  const stopImpersonating = async () => {
    if (!impersonation) return;
    const response = await axios.post(`${API_URL}/api/admin/stop-impersonate`, { email: impersonation.admin.email, audit_id: impersonation.audit_id }, { withCredentials: true });
    sessionStorage.removeItem(IMPERSONATION_KEY);
    setImpersonation(null);
    setUser(response.data);
    return response.data;
  };

  const trackImpersonationPage = useCallback(async (pagePath) => {
    if (!impersonation?.audit_id) return;
    try {
      await axios.post(`${API_URL}/api/admin/impersonate/track-page`, { audit_id: impersonation.audit_id, page: pagePath }, { withCredentials: true });
    } catch (err) {
      // Non-critical: silently fail page tracking
    }
  }, [impersonation]);

  const refreshToken = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
      await checkAuth();
    } catch (error) {
      _clearToken();
      setUser(false);
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      loginWithGoogle, 
      processGoogleSession, 
      logout, 
      refreshToken, 
      checkAuth,
      setUser,
      impersonation,
      impersonateUser,
      stopImpersonating,
      trackImpersonationPage
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Helper to format API error messages
export function formatApiError(error) {
  const detail = error.response?.data?.detail;
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}
